/**
 * Rotación Mental — replica RotacionTest.aspx.cs.
 *
 * Mecánica:
 * - Cada trial muestra dos imágenes (izquierda / derecha).
 * - El usuario responde Sí (mismo objeto) o No (diferente).
 * - Se mide el tiempo de reacción desde que aparecen las imágenes hasta el clic.
 * - Resultados: agrupados por grado de rotación → RT promedio de trials correctos.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTestConfig, TYPE_TO_API_PATH } from '../../api/client';
import TestLayout, {
  DoneScreen, YesNoButtons, ContinueButton, LoadingScreen, ErrorScreen, imageUrl,
} from './shared/TestLayout';
import SaveResults from './shared/SaveResults';
import { navigateAfterTest } from '../../api/ensayosClient';
import SafeHtml from './shared/SafeHtml';
import { registrarUso } from '../../api/usoClient';

const PHASE = { LOADING: 'loading', RUNNING: 'running', DONE: 'done' };

const IMAGE_BASE = (import.meta.env.VITE_IMAGE_URL ?? '').replace(/\/$/, '');

function RotImg({ src, alt }) {
  const [hidden, setHidden] = useState(false);
  if (!src || hidden) return null;
  return (
    <img
      src={src}
      alt={alt}
      className="max-h-56 object-contain"
      onError={() => setHidden(true)}
    />
  );
}

export default function TestRunRotacion() {
  const { lenguajeId, categoriaId, tareaId, type, testId } = useParams();
  const navigate = useNavigate();
  const apiType = TYPE_TO_API_PATH[type] ?? type;

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(PHASE.LOADING);
  const [seqIndex, setSeqIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const rtStartRef     = useRef(null);
  const usageLoggedRef = useRef(false);
  const responsesRef   = useRef([]);
  responsesRef.current = responses;

  useEffect(() => {
    getTestConfig(apiType, testId)
      .then((data) => {
        setConfig(data);
        (data.ListaValores?.Secuencia ?? []).forEach((item) => {
          [item.rutaImagen1, item.rutaImagen2].forEach((url) => {
            if (url) { const img = new Image(); img.src = imageUrl(url); }
          });
        });
        setPhase(PHASE.RUNNING);
      })
      .catch((e) => setError(e.message));
  }, [apiType, testId]);

  // Iniciar cronómetro cuando aparece un nuevo trial
  useEffect(() => {
    if (phase === PHASE.RUNNING) rtStartRef.current = performance.now();
  }, [phase, seqIndex]);

  const goBack = useCallback(() => {
    navigateAfterTest(
      navigate,
      `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}`,
    );
  }, [navigate, lenguajeId, categoriaId, tareaId]);

  useEffect(() => {
    if (phase !== PHASE.DONE || usageLoggedRef.current) return;
    usageLoggedRef.current = true;
    const r = responsesRef.current;
    registrarUso({
      tipo_test:   `R${testId}`,
      test_id:     testId,
      tarea_id:    tareaId,
      nombre_test: config?.descripcion ?? '',
      completada:  true,
      total_items: r.length,
      correctas:   r.filter((x) => x.correcto).length,
      errores:     r.filter((x) => !x.correcto).length,
    });
  }, [phase, testId, tareaId, config]);

  const handleExit = useCallback(() => {
    if (!usageLoggedRef.current) {
      usageLoggedRef.current = true;
      const r = responsesRef.current;
      registrarUso({
        tipo_test:   `R${testId}`,
        test_id:     testId,
        tarea_id:    tareaId,
        nombre_test: config?.descripcion ?? '',
        completada:  false,
        total_items: r.length,
        correctas:   r.filter((x) => x.correcto).length,
        errores:     r.filter((x) => !x.correcto).length,
      });
    }
    goBack();
  }, [goBack, testId, tareaId, config]);

  const secuencia = config?.ListaValores?.Secuencia ?? [];
  const conclusiones = config?.Concluciones ?? '';
  const currentItem = secuencia[seqIndex];

  const handleResponse = useCallback((userYes) => {
    const rt = Math.round(performance.now() - (rtStartRef.current ?? 0));
    const correct = currentItem.es_verdadero === userYes;
    setResponses((r) => [...r, {
      rotacion: currentItem.rotacion ?? 0,
      respuesta: userYes ? 'SI' : 'NO',
      correcto: correct,
      rt,
    }]);
    const next = seqIndex + 1;
    if (next >= secuencia.length) setPhase(PHASE.DONE);
    else setSeqIndex(next);
  }, [currentItem, seqIndex, secuencia.length]);

  const advanceConsigna = useCallback(() => {
    const next = seqIndex + 1;
    if (next >= secuencia.length) setPhase(PHASE.DONE);
    else setSeqIndex(next);
  }, [seqIndex, secuencia.length]);

  const handleRetry = () => {
    setSeqIndex(0); setResponses([]); setShowResults(false); setPhase(PHASE.RUNNING);
  };

  if (error) return <ErrorScreen message={error} onBack={goBack} />;
  if (phase === PHASE.LOADING || !config) return <LoadingScreen />;

  if (phase === PHASE.DONE) {
    // Agrupar por grado de rotación → RT promedio de correctos
    const byRotation = {};
    responses.forEach(({ rotacion, correcto, rt }) => {
      if (!byRotation[rotacion]) byRotation[rotacion] = { totalCorrectos: 0, sumRt: 0, total: 0 };
      byRotation[rotacion].total++;
      if (correcto) { byRotation[rotacion].totalCorrectos++; byRotation[rotacion].sumRt += rt; }
    });

    const registros = responses.map((r) => ({
      pregunta:        `Rotación ${r.rotacion}°`,
      respuesta:       r.correcto ? r.respuesta : (r.respuesta === 'SI' ? 'NO' : 'SI'),
      respuestaUsuario:r.respuesta,
      correcta:        r.correcto,
      tiempoRespuesta: String(r.rt),
    }));

    return (
      <DoneScreen conclusiones={showResults ? null : conclusiones} onRetry={handleRetry} onBack={goBack}>
        <SaveResults tipo={`R${testId}`} registros={registros} />
        {showResults ? (
          <div className="mt-6 w-full max-w-md mx-auto text-left">
            <h3 className="text-lg font-semibold mb-3 text-center">Resultados por grado de rotación</h3>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Rotación</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Correctos</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">RT prom. (ms)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byRotation).sort(([a], [b]) => Number(a) - Number(b)).map(([rot, d]) => (
                  <tr key={rot} className="border-t border-slate-200">
                    <td className="px-3 py-2 text-slate-700">{rot}°</td>
                    <td className="px-3 py-2 text-right text-slate-700">{d.totalCorrectos} / {d.total}</td>
                    <td className="px-3 py-2 text-right text-slate-700">
                      {d.totalCorrectos > 0 ? Math.round(d.sumRt / d.totalCorrectos) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer uppercase"
              >
                OCULTAR RESULTADOS
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowResults(true)}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 mt-2 cursor-pointer"
          >
            QUIERO SABER MIS RESULTADOS
          </button>
        )}
      </DoneScreen>
    );
  }

  if (!currentItem) { setPhase(PHASE.DONE); return null; }

  if (currentItem.es_consigna) {
    return (
      <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
        <SafeHtml
          as="p"
          html={currentItem.Valor}
          className="text-center text-slate-800 whitespace-pre-wrap leading-tight max-w-5xl mb-4"
          style={{ fontSize: '33px', fontWeight: 700 }}
        />
        <ContinueButton onClick={advanceConsigna} />
      </TestLayout>
    );
  }

  return (
    <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
      <div className="w-full max-w-3xl">
        <div className="flex gap-8 items-center justify-center flex-wrap mb-8">
          <RotImg src={imageUrl(currentItem.rutaImagen1)} alt="Imagen izquierda" />
          <RotImg src={imageUrl(currentItem.rutaImagen2)} alt="Imagen derecha" />
        </div>
        <p className="text-center text-slate-600 mb-2">¿Son el mismo objeto?</p>
        <YesNoButtons onYes={() => handleResponse(true)} onNo={() => handleResponse(false)} />
      </div>
    </TestLayout>
  );
}
